import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import NoChartDataView from "../no-chart-data-view/no-chart-data-view";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

interface IProps {
  borderColor1: string;
  borderColor2?: string;
  borderColor3?: string;
  borderColor4?: string;
  borderColor5?: string;
  backgroundColor1: string;
  backgroundColor2?: string;
  backgroundColor3?: string;
  backgroundColor4?: string;
  backgroundColor5?: string;
  label1: string;
  label2?: string;
  label3?: string;
  label4?: string;
  label5?: string;
  data1: Array<number | null>;
  data2?: Array<number | null>;
  data3?: Array<number | null>;
  data4?: Array<number | null>;
  data5?: Array<number | null>;
  yAxesUnit: string;
  yAxesUnitRight?: string;
  labels: Array<string>;
  hasSecondDataSet?: boolean;
  hasThirdDataSet?: boolean;
  hasForthDataSet?: boolean;
  hasFifthDataSet?: boolean;
  isChartDataAvailable?: boolean;
  height?: string;
  xAxisLabel?: string;
  isHideLegend?: boolean;
  labelCallback?: (context: TooltipItem<"line">) => string | string[];
  tooltipTitleCallback?: (context: TooltipItem<"line">[]) => string | string[];
}

const AreaChart = (props: IProps) => {
  const labels = props.labels;
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top" as const,
        align: "start" as const,
        display: props.isHideLegend ? false : true,
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          boxHeight: 7,
          font: {
            size: 14,
            family: "Poppins",
            weight: "normal" as const,
          },
          color: "#383941",
          generateLabels: (chart: any) => {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            labels.forEach((label: any) => {
              // Customizing the label bullet with respective border colors
              switch (label.datasetIndex) {
                case 0:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor1;
                  break;
                case 1:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor2;
                  break;
                case 2:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor3;
                  break;
                case 3:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor4;
                  break;
                case 4:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor5;
                  break;
                default:
                  label.pointStyle = "circle";
                  label.fillStyle = "";
                  break;
              }
            });
            return labels;
          },
        },
      },
      tooltip: {
        yAlign: "bottom" as const,
        titleAlign: "center" as const,
        backgroundColor: "#ffffff",
        titleColor: "#383941",
        bodyColor: "#69768B",
        bodyFont: {
          size: 12,
          family: "Poppins",
          weight: "normal" as const,
        },
        titleFont: { size: 10, family: "Poppins" },
        displayColors: false,
        callbacks: {
          title: props.tooltipTitleCallback
            ? props.tooltipTitleCallback
            : undefined,
          label: props.labelCallback
            ? props.labelCallback
            : (context: TooltipItem<"line">) => {
                let label = "";
                if (context.parsed.y) {
                  if (props.yAxesUnit === "Rs.") {
                    label = props.yAxesUnit + " " + context.parsed.y.toFixed(2);
                  } else {
                    label = context.parsed.y.toFixed(2) + " " + props.yAxesUnit;
                  }
                }
                return label;
              },
        },
      },
      datalabels: {
        display: false,
      },
      centerText: false,
    },
    scales: {
      x: {
        title: {
          text: props.xAxisLabel,
          display: props.xAxisLabel ? true : false,
        },
        grid: {
          display: false,
        },
      },
      "left-y-axis": {
        type: "linear" as const,
        position: "left" as const,
        beginAtZero: true,
        ticks: {
          callback: (value: any) => `${value} ${props.yAxesUnit}`,
        },
      },
      ...(props.hasFifthDataSet && {
        "right-y-axis": {
          type: "linear" as const,
          position: "right" as const,
          beginAtZero: true,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: (value: any) => `${value} ${props.yAxesUnitRight ?? ""}`,
          },
        },
      }),
    },
  };

  const legendMargin = {
    id: "legendMargin",
    beforeInit: (chart: any) => {
      const fitValue = chart.legend.fit;
      chart.legend.fit = () => {
        fitValue.bind(chart.legend)();
        return (chart.legend.height += 30);
      };
    },
  };

  const data1 = {
    labels,
    datasets: [
      {
        fill: true,
        label: props.label1,
        data: props.data1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        yAxisID: "left-y-axis",
      },
    ],
  };

  const data2 = {
    labels,
    datasets: [
      {
        fill: true,
        label: props.label1,
        data: props.data1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label2,
        data: props.data2,
        borderColor: props.borderColor2,
        backgroundColor: props.backgroundColor2,
        yAxisID: "left-y-axis",
      },
    ],
  };

  const data3 = {
    labels,
    datasets: [
      {
        fill: true,
        label: props.label1,
        data: props.data1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label2,
        data: props.data2,
        borderColor: props.borderColor2,
        backgroundColor: props.backgroundColor2,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label3,
        data: props.data3,
        borderColor: props.borderColor3,
        backgroundColor: props.backgroundColor3,
        yAxisID: "left-y-axis",
      },
    ],
  };

  const data4 = {
    labels,
    datasets: [
      {
        fill: true,
        label: props.label1,
        data: props.data1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label2,
        data: props.data2,
        borderColor: props.borderColor2,
        backgroundColor: props.backgroundColor2,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label3,
        data: props.data3,
        borderColor: props.borderColor3,
        backgroundColor: props.backgroundColor3,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label4,
        data: props.data4,
        borderColor: props.borderColor4,
        backgroundColor: props.backgroundColor4,
        yAxisID: "left-y-axis",
      },
    ],
  };

  const data5 = {
    labels,
    datasets: [
      {
        fill: true,
        label: props.label1,
        data: props.data1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label2,
        data: props.data2,
        borderColor: props.borderColor2,
        backgroundColor: props.backgroundColor2,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label3,
        data: props.data3,
        borderColor: props.borderColor3,
        backgroundColor: props.backgroundColor3,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label4,
        data: props.data4,
        borderColor: props.borderColor4,
        backgroundColor: props.backgroundColor4,
        yAxisID: "left-y-axis",
      },
      {
        fill: true,
        label: props.label5,
        data: props.data5,
        borderColor: props.borderColor5,
        backgroundColor: props.backgroundColor5,
        yAxisID: "right-y-axis",
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        height: props.height ? props.height : "100%",
      }}
    >
      <div
        style={{
          minWidth: props.isChartDataAvailable ? "calc(100vw - 350px)" : "100%",
          width: "1000px",
          minHeight: props.isChartDataAvailable
            ? "calc(100vh - 300px)"
            : "calc(100vh - 190px)",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        {props.isChartDataAvailable ? (
          <Line
            options={options}
            data={
              props.hasFifthDataSet
                ? data5
                : props.hasForthDataSet
                ? data4
                : props.hasThirdDataSet
                ? data3
                : props.hasSecondDataSet
                ? data2
                : data1
            }
            plugins={[legendMargin]}
          />
        ) : (
          <NoChartDataView />
        )}
      </div>
    </div>
  );
};

export default AreaChart;
